/**
 * wayai alerts — show a hub's active operational alerts (Status & Notices)
 *
 * The runtime connection/credential-health surface the web app shows, exposed to
 * code agents. This is the first thing to run when a hub "isn't behaving" (audio
 * not delivered, agent silent, a tool failing) — an invalid/expired provider
 * credential shows up here as a `connection_auth` alert (e.g. "OpenAI TTS · 401")
 * instead of forcing a guess from source code.
 *
 * Usage:
 *   wayai alerts                     # active alerts for the active hub
 *   wayai alerts --hub <uuid|name>   # for a specific hub (works without a workspace)
 *   wayai alerts --json              # raw JSON ({ hub_id, alerts })
 */

import { requireAuth } from '../lib/auth.js';
import { resolveActiveHubId } from '../lib/workspace.js';
import { ApiClient, ApiError } from '../lib/api-client.js';
import { wantsHelp } from '../lib/utils.js';
import { describeCategory, severityGlyph, activeAlerts, formatProviderBits, formatAlertTarget } from '../lib/hub-alerts.js';
import type { HubAlert } from '@wayai/core/contracts';

function printAlertsHelp(): void {
  console.error(`
wayai alerts — show a hub's active operational alerts (Status & Notices)

Usage:
  wayai alerts                     Active alerts for the active hub
  wayai alerts --hub <uuid|name>   For a specific hub (accepts a raw uuid without a workspace)
  wayai alerts --json              Raw JSON output

Alerts are tenant-actionable connection/credential health signals raised at
runtime — e.g. a provider rejecting a bad/expired key (\`connection_auth\`),
a rate/quota limit (\`connection_quota\`), an expired channel token
(\`channel_token\`), or a missing production credential. Run this FIRST when a
hub misbehaves (audio/TTS not delivered, agent not replying, a tool failing)
before reading code or filing a report.
`.trim());
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export async function alertsCommand(args: string[]): Promise<void> {
  if (wantsHelp(args)) {
    printAlertsHelp();
    return;
  }

  const jsonIdx = args.indexOf('--json');
  const jsonOutput = jsonIdx !== -1;
  if (jsonOutput) args.splice(jsonIdx, 1);

  // resolveActiveHubId consumes `--hub <value>` (uuid or workspace folder) and
  // accepts a raw uuid even without a materialized workspace — so this read-only
  // command works from anywhere with just a login.
  const hubId = resolveActiveHubId(args);
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  let alerts: HubAlert[];
  try {
    ({ alerts } = await client.getHubAlerts(hubId));
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      console.error(`Hub ${hubId} not found, or you lack read access to it.`);
      process.exit(1);
    }
    if (err instanceof ApiError && err.status === 403) {
      console.error(`You don't have read access to hub ${hubId}.`);
      process.exit(1);
    }
    throw err;
  }

  const active = activeAlerts(alerts);

  if (jsonOutput) {
    console.log(JSON.stringify({ hub_id: hubId, alerts: active }, null, 2));
    return;
  }

  if (active.length === 0) {
    console.log(`Hub ${hubId} — no active alerts ✓`);
    return;
  }

  // Enrich connection_id → a human label (connector name + type). Best-effort:
  // a failure here (transient / permissions) must not hide the alerts, so we
  // fall back to the raw connection_id.
  const connLabels = new Map<string, string>();
  try {
    const { connections } = await client.listConnections(hubId);
    for (const conn of connections) {
      if (!conn.connection_id) continue;
      const name = conn.connection_display_name || conn.connector_name;
      connLabels.set(conn.connection_id, `${name} (${conn.connector_type})`);
    }
  } catch {
    // degrade to raw ids
  }

  const noun = active.length === 1 ? 'alert' : 'alerts';
  console.log(`Hub ${hubId} — ${active.length} active ${noun}\n`);

  for (const a of active) {
    const { label, hint } = describeCategory(a.category);
    const target = formatAlertTarget(a, (id) => connLabels.get(id));
    const providerBits = formatProviderBits(a);

    console.log(`${severityGlyph(a.severity)} ${a.severity}  ${a.category} — ${label}`);
    // Append the credential-safe structured reason (type/code/param) when present —
    // never the body. Makes a config rejection self-diagnosable at a glance. gh #3164.
    console.log(`   ${target}${providerBits ? `  ·  ${providerBits}` : ''}${a.detail ? `  ·  ${a.detail}` : ''}`);
    console.log(`   ${hint}`);
    if (a.org_credential_id) {
      console.log(`   Shared org credential — fix it once at the org credential (\`wayai update-credential\`); it fans out to every hub linking it.`);
    }
    console.log(`   seen ${a.count}× · first ${fmtTime(a.first_seen_at)} · last ${fmtTime(a.last_seen_at)}`);
    console.log('');
  }
}
